import { useRouterState } from "@tanstack/react-router";
import {
	Button as AriaButton,
	Dialog as AriaDialog,
	DialogTrigger as AriaDialogTrigger,
	Popover as AriaPopover,
} from "react-aria-components";
import type {
	NavItemDividerType,
	NavItemType,
} from "@/components/application/app-navigation/config";
import { Tooltip } from "@/components/base/tooltip/tooltip";
import { cx } from "@/utils/cx";
import { useMobileNavClose } from "../LeftNav/MobileLeftNav";
import { NavItemBase } from "../NavItem/NavItem";
import { NavItemButton } from "../NavItemButton/NavItemButton";

interface CollapsedNavListProps {
	/** Additional CSS classes to apply to the list. */
	className?: string;
	/** List of items to display. */
	items: (NavItemType | NavItemDividerType)[];
	/** Custom background class for selected items */
	selectedClassName?: string;
	/** Custom class for the main level icons */
	iconClassName?: string;
	/** Override class for nav item hover background (e.g. on-brand alpha hover) */
	hoverClassName?: string;
	/** Brand (dark sidebar) visual treatment */
	variant?: "light" | "brand";
	/** Function to close mobile navigation when route is clicked */
	onMobileNavClose?: () => void;
}

const ICON_ONLY_BTN_BASE =
	"group relative flex cursor-pointer select-none items-center justify-center rounded-md outline-focus-ring transition duration-100 ease-linear focus-visible:z-10 focus-visible:outline-2 focus-visible:outline-offset-2 size-10 p-2";

/**
 * Icon-only nav list for the collapsed sidebar.
 * Parent sections open a right flyout of label-only children (no inline icon children).
 */
export const CollapsedNavList = ({
	items,
	className,
	selectedClassName,
	iconClassName = "text-gray-600",
	hoverClassName,
	variant = "light",
	onMobileNavClose,
}: CollapsedNavListProps) => {
	const mobileNavClose = useMobileNavClose();
	const isBrand = variant === "brand";

	const currentPath = useRouterState({
		select: (state) => state.location.pathname,
	});

	const closeMobileNav = mobileNavClose || onMobileNavClose;

	const iconBtnClass = cx(
		ICON_ONLY_BTN_BASE,
		hoverClassName ?? "hover:bg-primary_hover",
	);

	return (
		<ul className={cx("flex flex-col items-center", className)}>
			{items.map((item) => {
				if (item.divider) {
					return (
						<li key={item.label} className="w-full px-0.5 py-2">
							<hr className="h-px w-full border-none bg-border-secondary" />
						</li>
					);
				}

				if (item.items?.length) {
					const hasActiveChild = item.items.some(
						(child) =>
							child.href &&
							currentPath &&
							(currentPath === child.href ||
								currentPath.startsWith(`${child.href}/`)),
					);
					const ParentIcon = item.icon;

					return (
						<li
							key={item.label}
							className="flex w-full appearance-none justify-center py-0.5"
						>
							<Tooltip title={item.label} placement="right">
								<AriaDialogTrigger>
									<AriaButton
										className={cx(
											iconBtnClass,
											hasActiveChild && selectedClassName,
										)}
										data-testid={item.testId}
										aria-haspopup="menu"
										aria-label={item.label}
									>
										{ParentIcon && (
											<ParentIcon
												aria-hidden="true"
												className={cx(
													"size-5 shrink-0 transition-inherit-all",
													iconClassName,
												)}
											/>
										)}
									</AriaButton>

									<AriaPopover
										placement="right"
										offset={12}
										className={({ isEntering, isExiting }) =>
											cx(
												"origin-(--trigger-anchor-point) will-change-transform",
												isEntering &&
													"duration-150 ease-out animate-in fade-in placement-right:slide-in-from-left-0.5",
												isExiting &&
													"duration-100 ease-in animate-out fade-out placement-right:slide-out-to-left-0.5",
											)
										}
									>
										<AriaDialog
											aria-label={item.label}
											className={cx(
												"outline-hidden rounded-2xl shadow-lg ring ring-secondary_alt p-2 min-w-[200px]",
												isBrand
													? "bg-[#1a2d40] border border-[rgba(255,255,255,0.1)]"
													: "bg-primary_alt",
											)}
										>
											{({ close }) => (
												<ul className="flex flex-col">
													{item.items?.map((childItem) => (
														<li key={childItem.label} className="py-0.5">
															{/* Label-only links in flyout (no icon, no expanded-nav indent) */}
															<NavItemBase
																href={childItem.href}
																badge={childItem.badge}
																testId={childItem.testId}
																type="link"
																variant={variant}
																selectedClassName={selectedClassName}
																hoverClassName={hoverClassName}
																onClick={() => {
																	close();
																	closeMobileNav?.();
																}}
															>
																{childItem.label}
															</NavItemBase>
														</li>
													))}
												</ul>
											)}
										</AriaDialog>
									</AriaPopover>
								</AriaDialogTrigger>
							</Tooltip>
						</li>
					);
				}

				return (
					<li key={item.label} className="flex w-full justify-center py-0.5">
						<NavItemButton
							type="link"
							icon={item.icon}
							href={item.href}
							testId={item.testId}
							selectedClassName={selectedClassName}
							iconClassName={iconClassName}
							hoverClassName={hoverClassName}
							label={item.label}
							onClick={() => closeMobileNav?.()}
						>
							{item.label}
						</NavItemButton>
					</li>
				);
			})}
		</ul>
	);
};
